import React, { useState } from 'react';
import { format } from 'date-fns';
import { useField } from 'informed';

import { DATE_PICKER_DATE_FORMAT } from '@nw/shared/config';
import { ChevronDownIcon } from '@nw/shared/ui';

import { STRING_DELIMITER } from '../../../constants/MiscellaneousConstants';
import { Option as RadioGroupOption } from '../../../types';

import { Radio } from '../../Radio';

import {
  ChevronIconWrapper,
  DayLabel,
  Divider,
  ErrorText,
  FieldContainer,
  Label,
  LabelAndCloseIconWrapper,
  Option,
  OptionsContainer,
  radioWrapperStyles,
  SelectWebinarSlotModalViewContainer,
  ShowMoreButton,
  SlotCard,
  SlotCardsContainer,
  SlotOptionText,
  SlotTime,
  StyledTickIcon,
  TickIconContainer,
  TimeSlotsContainer
} from './styledComponents';

interface SlotSelectionFieldFormattedOptionProps {
  field: string;
  supportingField: string;
  label?: string;
  options: any;
  validationSchemaObject: any;
  error: string;
  touched: boolean;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  slotUIType?: string;
  containerClassName?: string;
  labelClassName?: string;
  executeChildFunction: React.MutableRefObject<(value: string) => void | null>;
  showAllSlotsState?: boolean;
}

interface OptionType {
  value: string;
  label: string;
}

interface LabelAndOptionsType {
  date: Date;
  dayCode: number;
  label: string;
  options: OptionType[];
}

interface TimeSlotOption {
  label: string;
  formattedOptions: OptionType[];
  dayCode: number;
  date: Date;
  options: any[];
}

export const SlotSelectionFieldFormattedOption = (
  props: SlotSelectionFieldFormattedOptionProps
) => {
  const {
    field,
    label,
    options,
    validationSchemaObject,
    error,
    touched,
    validateOnBlur = true,
    validateOnChange = true,
    supportingField = '',
    slotUIType,
    containerClassName,
    labelClassName,
    executeChildFunction,
    showAllSlotsState = false
  } = props;

  const { fieldState, fieldApi } = useField({
    field,
    validateOnBlur,
    validateOnChange,
    validationSchema: validationSchemaObject.fields[field],
    error: error,
    touched: touched,
    options
  });

  const { fieldApi: supportingFieldApi } = useField({
    field: supportingField,
    validateOnBlur,
    validateOnChange,
    error: '',
    touched: false
  });

  const { value } = fieldState;
  const { setValue, setTouched } = fieldApi;

  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedValue, setSelectedValue] = useState<string>();
  const [showAllSlots, setShowAllSlots] = useState<boolean>(false);

  const updateSelectedValue = (value: string): void => {
    setSelectedValue(value);
  };
  executeChildFunction.current = updateSelectedValue;

  const onClickOption = (option: OptionType | string, date: Date): void => {
    let value: string;
    setSelectedDate(date);
    if (typeof option !== 'string') {
      value = option.value;
    } else {
      value = option;
    }
    setSelectedValue(value);
    value = value.split(STRING_DELIMITER)[1];
    setValue(value);
    setTouched(true);

    const webinar1DateTimeValue = format(date, DATE_PICKER_DATE_FORMAT);

    supportingFieldApi.setValue(webinar1DateTimeValue);
  };

  const isOptionSelected = (option: OptionType, date: Date): boolean => {
    return (
      option.value.split(STRING_DELIMITER)[1] === value &&
      String(date) === String(selectedDate)
    );
  };

  const shouldRenderDividerLine = (index: number): boolean => {
    return index < options.length - 1 && options.length > 1;
  };

  const shouldShowToggleButton = (): boolean => {
    return showAllSlotsState && options.length > 2;
  };

  const toggleShowAllSlots = (): void => {
    setShowAllSlots((prev) => !prev);
  };

  const renderRadioGroupSlotUIType = (
    values: LabelAndOptionsType
  ): React.ReactElement => {
    const { label, options, date } = values;

    // Convert OptionType[] to BaseRadioGroupOption[]
    const radioOptions: RadioGroupOption[] = options.map((opt) => ({
      label: opt.label,
      value: opt.value
    }));

    return (
      <FieldContainer key={String(date)}>
        <Radio
          name={field}
          label={label}
          value={selectedValue}
          options={radioOptions}
          onChange={(optionValue) => onClickOption(optionValue, date)}
          orientation="horizontal"
          labelClassName="radioGroupLabelClassName"
          radioClassName="radioButtonClassName"
          className="mb-0"
        />
      </FieldContainer>
    );
  };

  const renderCardSlotUIType = (
    values: LabelAndOptionsType
  ): React.ReactElement => {
    const { options, date } = values;
    return (
      <SlotCardsContainer>
        {options.map((option) => {
          const { label, value } = option;
          const isSelected = isOptionSelected(option, date);
          return (
            <SlotCard
              key={value}
              onClick={() => onClickOption(option, date)}
              isSelected={isSelected}
              type="button"
              className={
                isSelected
                  ? 'slotCardClassName slotCardSelectedClassName'
                  : 'slotCardClassName'
              }
            >
              <SlotTime>{label}</SlotTime>
            </SlotCard>
          );
        })}
      </SlotCardsContainer>
    );
  };

  const renderLabelAndOptions = (
    values: LabelAndOptionsType
  ): React.ReactElement => {
    const { label, options, date } = values;

    if (slotUIType === 'CARD') {
      return renderCardSlotUIType(values);
    } else if (slotUIType) {
      return renderRadioGroupSlotUIType(values);
    }

    return (
      <TimeSlotsContainer>
        <LabelAndCloseIconWrapper>
          <DayLabel className={'dayLabelClassName'}>{label}</DayLabel>
        </LabelAndCloseIconWrapper>
        <OptionsContainer>
          {options.map((option) => {
            const { label } = option;
            const isSelected = isOptionSelected(option, date);
            return (
              <Option
                key={option.value}
                onClick={() => onClickOption(option, date)}
                isSelected={isSelected}
                type="button"
                className={
                  isSelected
                    ? 'slotOptionsContainerClassName slotSelectedOptionsContainerClassName'
                    : 'slotOptionsContainerClassName'
                }
              >
                <TickIconContainer
                  width={16}
                  className={'tickIconContainerClassName'}
                >
                  {isSelected ? <StyledTickIcon width={16} /> : null}
                </TickIconContainer>
                <SlotOptionText
                  isSelected={isSelected}
                  className={
                    isSelected ? 'slotOptionSelectedTextClassName' : ''
                  }
                >
                  {label}
                </SlotOptionText>
              </Option>
            );
          })}
        </OptionsContainer>
      </TimeSlotsContainer>
    );
  };

  return (
    <>
      <Label className={`${labelClassName} slotSelectionLabelClassName`}>
        {label}
      </Label>
      <SelectWebinarSlotModalViewContainer className={containerClassName}>
        {options.map((timeSlotOption: TimeSlotOption, index: number) => {
          const shouldHide = showAllSlotsState && !showAllSlots && index > 1;
          return (
            timeSlotOption.options.length > 0 && (
              <React.Fragment key={index}>
                {showAllSlotsState ? (
                  <div style={{ display: shouldHide ? 'none' : 'block' }}>
                    {renderLabelAndOptions({
                      label: timeSlotOption.label,
                      options: timeSlotOption.formattedOptions,
                      dayCode: timeSlotOption.dayCode,
                      date: timeSlotOption.date
                    })}

                    {shouldRenderDividerLine(index) && !slotUIType && (
                      <Divider />
                    )}
                  </div>
                ) : (
                  <>
                    {renderLabelAndOptions({
                      label: timeSlotOption.label,
                      options: timeSlotOption.formattedOptions,
                      dayCode: timeSlotOption.dayCode,
                      date: timeSlotOption.date
                    })}

                    {shouldRenderDividerLine(index) && !slotUIType && (
                      <Divider />
                    )}
                  </>
                )}
              </React.Fragment>
            )
          );
        })}
        <div
          id="show-more-button-container"
          className="flex flex-col align-start"
        >
          {touched && error && <ErrorText>{error}</ErrorText>}
          {shouldShowToggleButton() && (
            <ShowMoreButton
              type="button"
              onClick={toggleShowAllSlots}
              id="show-more-button"
              className="align-self-start text-left"
            >
              {showAllSlots ? 'Hide' : 'Show more slots'}
              <ChevronIconWrapper isExpanded={showAllSlots}>
                <ChevronDownIcon width={16} height={16} stroke="#4f46e5" />
              </ChevronIconWrapper>
            </ShowMoreButton>
          )}
        </div>
      </SelectWebinarSlotModalViewContainer>
    </>
  );
};
